import {
  loadHomeRailMenuLayoutFromIdb,
  saveHomeRailMenuLayoutToIdb,
} from "@/gm/gm-monster-catalog-idb";
import { homeIcon } from "@/home/home-icons";
import { homeRailIcon } from "@/home/home-rail-icons";
import type { HomeRailIconName } from "@/home/home-rail-icons";

export const HOME_RAIL_MENU_LAYOUT_VERSION = 2 as const;

export type HomeRailMenuId =
  | "decorate"
  | "character"
  | "skill"
  | "tower"
  | "dungeon"
  | "raid"
  | "rank"
  | "shop"
  | "stash";

export const HOME_RAIL_MENU_IDS: readonly HomeRailMenuId[] = [
  "decorate",
  "character",
  "skill",
  "tower",
  "dungeon",
  "raid",
  "rank",
  "shop",
  "stash",
];

export type HomeRailMenuItemConfig = {
  /** ปิด = ไม่แสดงในเกมเลย */
  enabled: boolean;
  /** กินเต็มความกว้างเมื่อขยายเมนู */
  fullWidthExpanded: boolean;
};

export type HomeRailMenuLayout = {
  version: typeof HOME_RAIL_MENU_LAYOUT_VERSION;
  columnsCollapsed: number;
  columnsExpanded: number;
  orderCollapsed: HomeRailMenuId[];
  orderExpanded: HomeRailMenuId[];
  items: Record<HomeRailMenuId, HomeRailMenuItemConfig>;
};

export type HomeRailEditMode = "collapsed" | "expanded";

export type HomeRailMenuFeatures = {
  skillsModals: boolean;
  cashShop: boolean;
  walletHud: boolean;
};

const DEFAULT_ITEM: HomeRailMenuItemConfig = {
  enabled: true,
  fullWidthExpanded: false,
};

export const DEFAULT_HOME_RAIL_MENU_LAYOUT: HomeRailMenuLayout = {
  version: HOME_RAIL_MENU_LAYOUT_VERSION,
  columnsCollapsed: 1,
  columnsExpanded: 2,
  orderCollapsed: ["decorate", "character", "skill", "tower"],
  orderExpanded: [...HOME_RAIL_MENU_IDS],
  items: {
    decorate: { enabled: true, fullWidthExpanded: false },
    character: { enabled: true, fullWidthExpanded: false },
    skill: { enabled: true, fullWidthExpanded: false },
    tower: { enabled: true, fullWidthExpanded: false },
    dungeon: { enabled: true, fullWidthExpanded: false },
    raid: { enabled: true, fullWidthExpanded: true },
    rank: { enabled: true, fullWidthExpanded: false },
    shop: { enabled: true, fullWidthExpanded: false },
    stash: { enabled: true, fullWidthExpanded: true },
  },
};

let layoutCache: HomeRailMenuLayout = { ...DEFAULT_HOME_RAIL_MENU_LAYOUT };

const MENU_META: Record<
  HomeRailMenuId,
  {
    labelTh: string;
    icon: HomeRailIconName;
    elementId: string;
    kind: "button" | "link";
    href?: string;
    feature?: keyof HomeRailMenuFeatures;
  }
> = {
  decorate: {
    labelTh: "แต่ง",
    icon: "decorate",
    elementId: "home-enter-edit",
    kind: "button",
  },
  character: {
    labelTh: "ตัวละคร",
    icon: "character",
    elementId: "home-open-character",
    kind: "button",
  },
  skill: {
    labelTh: "สกิล",
    icon: "skill",
    elementId: "home-open-skills",
    kind: "button",
    feature: "skillsModals",
  },
  tower: {
    labelTh: "หอ",
    icon: "tower",
    elementId: "home-open-tower",
    kind: "button",
  },
  dungeon: {
    labelTh: "ด่าน",
    icon: "dungeon",
    elementId: "home-open-dungeon",
    kind: "button",
  },
  raid: {
    labelTh: "โจมตี",
    icon: "raid",
    elementId: "home-open-raid",
    kind: "button",
  },
  rank: {
    labelTh: "อันดับ",
    icon: "rank",
    elementId: "home-open-rank",
    kind: "button",
  },
  shop: {
    labelTh: "ร้าน",
    icon: "shop",
    elementId: "home-open-currency-shop",
    kind: "button",
    feature: "cashShop",
  },
  stash: {
    labelTh: "คลัง",
    icon: "stash",
    elementId: "home-open-stash",
    kind: "button",
    feature: "walletHud",
  },
};

export function homeRailMenuMeta(id: HomeRailMenuId) {
  return MENU_META[id];
}

export function homeRailMenuLabelTh(id: HomeRailMenuId): string {
  return MENU_META[id].labelTh;
}

function clampColumns(n: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(3, Math.max(1, Math.floor(n)));
}

function mergeOrderList(raw: unknown, fallback: HomeRailMenuId[]): HomeRailMenuId[] {
  const order: HomeRailMenuId[] = [];
  if (Array.isArray(raw)) {
    for (const id of raw) {
      if (HOME_RAIL_MENU_IDS.includes(id as HomeRailMenuId) && !order.includes(id as HomeRailMenuId)) {
        order.push(id as HomeRailMenuId);
      }
    }
  }
  for (const id of fallback) {
    if (!order.includes(id)) order.push(id);
  }
  return order;
}

type LegacyRailLayout = {
  version?: number;
  columns?: number;
  order?: HomeRailMenuId[];
  items?: Record<
    string,
    { enabled?: boolean; collapsed?: boolean; fullWidth?: boolean; fullWidthExpanded?: boolean }
  >;
};

export function normalizeHomeRailMenuLayout(
  raw: Partial<HomeRailMenuLayout> | LegacyRailLayout | null | undefined,
): HomeRailMenuLayout {
  const base = DEFAULT_HOME_RAIL_MENU_LAYOUT;
  const legacy = raw as LegacyRailLayout | undefined;

  let orderCollapsed = base.orderCollapsed;
  let orderExpanded = base.orderExpanded;
  let columnsCollapsed = base.columnsCollapsed;
  let columnsExpanded = base.columnsExpanded;

  if (raw && raw.version === HOME_RAIL_MENU_LAYOUT_VERSION && "orderCollapsed" in raw) {
    orderCollapsed = mergeOrderList(raw.orderCollapsed, []);
    orderExpanded = mergeOrderList(raw.orderExpanded, []);
    columnsCollapsed = clampColumns(raw.columnsCollapsed ?? base.columnsCollapsed, base.columnsCollapsed);
    columnsExpanded = clampColumns(raw.columnsExpanded ?? base.columnsExpanded, base.columnsExpanded);
  } else if (legacy?.order) {
    const order = mergeOrderList(legacy.order, base.orderExpanded);
    orderExpanded = order;
    orderCollapsed = order.filter((id) => legacy.items?.[id]?.collapsed === true);
    if (orderCollapsed.length === 0) {
      orderCollapsed = base.orderCollapsed;
    }
    columnsExpanded = clampColumns(legacy.columns ?? base.columnsExpanded, base.columnsExpanded);
    columnsCollapsed = 1;
  }

  const items = { ...base.items };
  for (const id of HOME_RAIL_MENU_IDS) {
    const patch = legacy?.items?.[id] ?? raw?.items?.[id];
    const legacyFull = patch && "fullWidth" in patch ? patch.fullWidth : undefined;
    items[id] = {
      enabled: patch?.enabled ?? items[id]?.enabled ?? DEFAULT_ITEM.enabled,
      fullWidthExpanded:
        patch?.fullWidthExpanded ??
        legacyFull ??
        items[id]?.fullWidthExpanded ??
        DEFAULT_ITEM.fullWidthExpanded,
    };
  }

  const synced = syncCollapsedExpandedOrders(orderCollapsed, orderExpanded);

  return {
    version: HOME_RAIL_MENU_LAYOUT_VERSION,
    columnsCollapsed,
    columnsExpanded,
    orderCollapsed: synced.orderCollapsed,
    orderExpanded: synced.orderExpanded,
    items,
  };
}

/** แบบย่อต้องเป็น subset ของแบบขยาย — กันเมนูค้างในแบบย่อที่ GM เอาออกจากแบบขยายแล้ว */
function syncCollapsedExpandedOrders(
  orderCollapsed: HomeRailMenuId[],
  orderExpanded: HomeRailMenuId[],
): { orderCollapsed: HomeRailMenuId[]; orderExpanded: HomeRailMenuId[] } {
  const expandedSet = new Set(orderExpanded);
  return {
    orderCollapsed: orderCollapsed.filter((id) => expandedSet.has(id)),
    orderExpanded,
  };
}

export function homeRailOrderForMode(
  layout: HomeRailMenuLayout,
  mode: HomeRailEditMode,
): HomeRailMenuId[] {
  return mode === "collapsed" ? layout.orderCollapsed : layout.orderExpanded;
}

export function homeRailColumnsForMode(layout: HomeRailMenuLayout, mode: HomeRailEditMode): number {
  return mode === "collapsed" ? layout.columnsCollapsed : layout.columnsExpanded;
}

export function setHomeRailOrderForMode(
  layout: HomeRailMenuLayout,
  mode: HomeRailEditMode,
  order: HomeRailMenuId[],
): HomeRailMenuLayout {
  const sanitized = mergeOrderList(order, []);
  return mode === "collapsed"
    ? { ...layout, orderCollapsed: sanitized }
    : { ...layout, orderExpanded: sanitized };
}

export function isHomeRailIdInMode(
  layout: HomeRailMenuLayout,
  id: HomeRailMenuId,
  mode: HomeRailEditMode,
): boolean {
  return homeRailOrderForMode(layout, mode).includes(id);
}

export function homeRailHasExpandableMenus(
  layout: HomeRailMenuLayout,
  features: HomeRailMenuFeatures,
): boolean {
  const collapsed = new Set(
    resolvedHomeRailMenuOrder(layout, features, "collapsed").map((x) => x),
  );
  const expanded = resolvedHomeRailMenuOrder(layout, features, "expanded");
  return expanded.some((id) => !collapsed.has(id));
}

export function getHomeRailMenuLayout(): HomeRailMenuLayout {
  return layoutCache;
}

export async function hydrateHomeRailMenuLayout(): Promise<HomeRailMenuLayout> {
  try {
    const raw = await loadHomeRailMenuLayoutFromIdb();
    layoutCache = normalizeHomeRailMenuLayout(raw ?? undefined);
  } catch {
    layoutCache = normalizeHomeRailMenuLayout(undefined);
  }
  return layoutCache;
}

export async function saveHomeRailMenuLayout(layout: HomeRailMenuLayout): Promise<void> {
  const next = normalizeHomeRailMenuLayout(layout);
  layoutCache = next;
  await saveHomeRailMenuLayoutToIdb(next);
}

export function resetHomeRailMenuLayoutCache(): void {
  layoutCache = normalizeHomeRailMenuLayout(undefined);
}

export function isHomeRailMenuAvailable(
  id: HomeRailMenuId,
  features: HomeRailMenuFeatures,
): boolean {
  const meta = MENU_META[id];
  if (!meta.feature) return true;
  return features[meta.feature];
}

export function resolvedHomeRailMenuOrder(
  layout: HomeRailMenuLayout,
  features: HomeRailMenuFeatures,
  mode: HomeRailEditMode,
): HomeRailMenuId[] {
  return homeRailOrderForMode(layout, mode).filter((id) => {
    if (!layout.items[id]?.enabled) return false;
    return isHomeRailMenuAvailable(id, features);
  });
}

export function allEnabledHomeRailMenuIds(
  layout: HomeRailMenuLayout,
  features: HomeRailMenuFeatures,
): HomeRailMenuId[] {
  const seen = new Set<HomeRailMenuId>();
  const out: HomeRailMenuId[] = [];
  for (const id of [...layout.orderCollapsed, ...layout.orderExpanded]) {
    if (seen.has(id)) continue;
    if (!layout.items[id]?.enabled) continue;
    if (!isHomeRailMenuAvailable(id, features)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export type HomeRailGridPlacement = {
  id: HomeRailMenuId;
  row: number;
  col: number;
  colSpan: number;
};

export function computeHomeRailGridPlacements(
  layout: HomeRailMenuLayout,
  features: HomeRailMenuFeatures,
  expanded: boolean,
): HomeRailGridPlacement[] {
  const mode: HomeRailEditMode = expanded ? "expanded" : "collapsed";
  const columns = clampColumns(
    homeRailColumnsForMode(layout, mode),
    homeRailColumnsForMode(layout, mode),
  );
  const ids = resolvedHomeRailMenuOrder(layout, features, mode);

  const out: HomeRailGridPlacement[] = [];
  let row = 1;
  let col = 1;

  for (const id of ids) {
    const item = layout.items[id];
    const span = expanded && item.fullWidthExpanded ? columns : 1;
    if (span > 1) {
      if (col > 1) {
        row += 1;
        col = 1;
      }
      out.push({ id, row, col: 1, colSpan: columns });
      row += 1;
      col = 1;
      continue;
    }
    out.push({ id, row, col, colSpan: 1 });
    col += 1;
    if (col > columns) {
      col = 1;
      row += 1;
    }
  }

  return out;
}

export function applyHomeRailGridLayout(
  frame: HTMLElement,
  layout: HomeRailMenuLayout,
  features: HomeRailMenuFeatures,
  expanded: boolean,
): void {
  const mode: HomeRailEditMode = expanded ? "expanded" : "collapsed";
  const columns = homeRailColumnsForMode(layout, mode);
  frame.style.setProperty("--home-rail-cols", String(columns));

  const placements = computeHomeRailGridPlacements(layout, features, expanded);
  const placed = new Set<HomeRailMenuId>();

  for (const p of placements) {
    placed.add(p.id);
    const el = frame.querySelector<HTMLElement>(`[data-rail-id="${p.id}"]`);
    if (!el) continue;
    el.style.gridRow = String(p.row);
    if (p.colSpan > 1) {
      el.style.gridColumn = "1 / -1";
    } else {
      el.style.gridColumn = String(p.col);
    }
    el.hidden = false;
  }

  for (const id of HOME_RAIL_MENU_IDS) {
    if (placed.has(id)) continue;
    const el = frame.querySelector<HTMLElement>(`[data-rail-id="${id}"]`);
    if (!el) continue;
    el.hidden = true;
    el.style.removeProperty("grid-row");
    el.style.removeProperty("grid-column");
  }
}

function renderRailCell(
  id: HomeRailMenuId,
  extraClass: string,
  attrs: string,
): string {
  const meta = MENU_META[id];
  const tag = meta.kind === "link" ? "a" : "button";
  const typeAttr = meta.kind === "button" ? ' type="button"' : "";
  const hrefAttr = meta.kind === "link" && meta.href ? ` href="${meta.href}"` : "";
  const pressedAttr =
    id === "decorate" ? ' aria-pressed="false"' : "";
  return `
        <${tag}${typeAttr} id="${meta.elementId}" class="home-rail-btn home-rail-btn--tile home-rail-cell ${extraClass}" data-rail-id="${id}"${hrefAttr}${pressedAttr} ${attrs}>
          ${homeRailIcon(meta.icon)}
          <span class="home-rail-label">${meta.labelTh}</span>
        </${tag}>`;
}

export function renderHomeRailFrameMarkup(features: HomeRailMenuFeatures): string {
  const layout = getHomeRailMenuLayout();
  const collapsedSet = new Set(resolvedHomeRailMenuOrder(layout, features, "collapsed"));
  const cells = allEnabledHomeRailMenuIds(layout, features)
    .map((id) => {
      const inCollapsed = collapsedSet.has(id);
      const extraClass = inCollapsed ? "" : "home-rail-cell--extra";
      const hiddenAttr = inCollapsed ? "" : "hidden";
      const ariaBits =
        id === "decorate"
          ? 'aria-label="แต่งบ้าน"'
          : id === "character"
            ? 'aria-label="ตัวละคร"'
            : id === "skill"
              ? 'aria-label="จัดการสกิล" title="จัดการสกิล"'
              : id === "tower"
                ? 'aria-label="หอต่อสู้"'
                : id === "dungeon"
                  ? 'aria-label="ด่านป้องกัน"'
                  : id === "raid"
                    ? 'aria-label="สุ่มโจมตีด่าน"'
                    : id === "rank"
                      ? 'aria-label="อันดับโลก"'
                      : id === "shop"
                        ? 'aria-label="ร้าน" title="ร้าน"'
                        : 'aria-label="คลัง" title="คลังของใช้"';
      const hidden = hiddenAttr ? " hidden" : "";
      return renderRailCell(id, extraClass, `${ariaBits}${hidden}`);
    })
    .join("");

  const hasExtra = homeRailHasExpandableMenus(layout, features);

  return `
      <div class="home-rail-stack">
      <div class="home-rail-frame" id="home-rail-frame">
        ${cells}
      </div>
      ${
        hasExtra
          ? `<button type="button" id="home-rail-more-toggle" class="home-rail-btn home-rail-btn--tile home-rail-btn--mini home-rail-more-toggle" aria-expanded="false" aria-controls="home-rail-frame" aria-label="ขยายเมนูเพิ่มเติม">
        ${homeRailIcon("more")}
        <span class="home-rail-label">เมนูเพิ่มเติม</span>
      </button>`
          : ""
      }
      </div>`;
}

/** GM preview assumes all feature-gated menus are available. */
export const HOME_RAIL_GM_PREVIEW_FEATURES: HomeRailMenuFeatures = {
  skillsModals: true,
  cashShop: true,
  walletHud: true,
};

export function moveHomeRailMenuIdInOrder(
  order: HomeRailMenuId[],
  id: HomeRailMenuId,
  beforeId: HomeRailMenuId | null,
): HomeRailMenuId[] {
  const filtered = order.filter((x) => x !== id);
  if (!beforeId) {
    filtered.push(id);
    return filtered;
  }
  const idx = filtered.indexOf(beforeId);
  if (idx < 0) {
    filtered.push(id);
    return filtered;
  }
  filtered.splice(idx, 0, id);
  return filtered;
}

/** Swap two slots in order, or replace one grid slot when the other id is only on the bar. */
export function replaceHomeRailMenuInOrder(
  order: HomeRailMenuId[],
  draggedId: HomeRailMenuId,
  targetId: HomeRailMenuId,
): HomeRailMenuId[] {
  if (draggedId === targetId) return order;
  const dragIdx = order.indexOf(draggedId);
  const targetIdx = order.indexOf(targetId);
  if (dragIdx >= 0 && targetIdx >= 0) {
    const next = [...order];
    next[dragIdx] = targetId;
    next[targetIdx] = draggedId;
    return next;
  }
  if (dragIdx < 0 && targetIdx >= 0) {
    const next = [...order];
    next[targetIdx] = draggedId;
    return next;
  }
  if (dragIdx >= 0 && targetIdx < 0) {
    const next = [...order];
    next[dragIdx] = targetId;
    return next;
  }
  return order;
}

export function renderGmRailPreviewMarkup(
  layout: HomeRailMenuLayout,
  mode: HomeRailEditMode,
): string {
  const orderIds = resolvedHomeRailMenuOrder(layout, HOME_RAIL_GM_PREVIEW_FEATURES, mode);
  const cells = orderIds
    .map((id) => {
      const meta = MENU_META[id];
      return `<div class="gm-rail-preview-slot" data-rail-id="${id}">
          <button type="button" class="home-rail-btn home-rail-btn--tile home-rail-cell gm-rail-preview-cell" data-rail-id="${id}" draggable="true" aria-label="${meta.labelTh}">
            ${homeRailIcon(meta.icon)}
            <span class="home-rail-label">${meta.labelTh}</span>
          </button>
          <button type="button" class="gm-rail-preview-remove gm-icon-tool gm-icon-tool--danger" data-rail-remove="${id}" draggable="false" aria-label="ลบออกจากแบบนี้" title="ลบออกจากแบบนี้">${homeIcon("trash")}</button>
        </div>`;
    })
    .join("");

  const emptyDrop =
    orderIds.length === 0
      ? `<div class="gm-rail-preview-drop-empty" data-rail-drop-zone="true" aria-label="วางเมนูที่นี่">ลากเมนูจากแถบล่างมาวาง</div>`
      : "";

  const showMore = mode === "collapsed" && homeRailHasExpandableMenus(layout, HOME_RAIL_GM_PREVIEW_FEATURES);

  return `
    <div class="home-rail-stack gm-rail-preview-stack">
      <div class="home-rail-frame gm-rail-preview-frame" id="gm-rail-preview-frame" data-rail-drop-zone="true">
        ${cells}${emptyDrop}
      </div>
      ${
        showMore
          ? `<button type="button" class="home-rail-btn home-rail-btn--tile home-rail-btn--mini home-rail-more-toggle gm-rail-preview-more-dummy" disabled aria-hidden="true">
        ${homeRailIcon("more")}
        <span class="home-rail-label">เมนูเพิ่มเติม</span>
      </button>`
          : ""
      }
    </div>`;
}

export function renderGmRailMenuBarMarkup(
  layout: HomeRailMenuLayout,
  mode: HomeRailEditMode,
  selectedId: HomeRailMenuId | null,
): string {
  const inMode = new Set(homeRailOrderForMode(layout, mode));
  const chips = HOME_RAIL_MENU_IDS.map((id) => {
    const meta = MENU_META[id];
    const enabled = layout.items[id]?.enabled ?? false;
    const active = enabled && inMode.has(id);
    const selected = id === selectedId;
    return `<button type="button" class="gm-rail-bar-chip${active ? " gm-rail-bar-chip--active" : ""}${enabled ? "" : " gm-rail-bar-chip--off"}${selected ? " gm-rail-bar-chip--selected" : ""}" data-bar-id="${id}" draggable="${enabled ? "true" : "false"}" aria-pressed="${active ? "true" : "false"}">
      <span class="gm-rail-bar-chip-art" aria-hidden="true">${homeRailIcon(meta.icon)}</span>
      <span class="gm-rail-bar-chip-label">${meta.labelTh}</span>
      <span class="gm-rail-bar-chip-eye" data-bar-toggle-enabled="${id}" title="${enabled ? "ปิดไม่แสดง" : "เปิดแสดง"}">${enabled ? "◉" : "○"}</span>
    </button>`;
  });
  return `<div class="gm-rail-menu-bar" id="gm-rail-menu-bar" role="toolbar" aria-label="รายการเมนู">${chips.join("")}</div>`;
}

export function paintGmRailPreviewDom(
  host: HTMLElement,
  layout: HomeRailMenuLayout,
  mode: HomeRailEditMode,
  selectedId: HomeRailMenuId | null,
): void {
  host.innerHTML = renderGmRailPreviewMarkup(layout, mode);
  const frame = host.querySelector<HTMLElement>("#gm-rail-preview-frame");
  if (frame) {
    applyHomeRailGridLayout(frame, layout, HOME_RAIL_GM_PREVIEW_FEATURES, mode === "expanded");
  }
  const stack = host.querySelector(".gm-rail-preview-stack");
  stack?.classList.toggle("home-rail--expanded", mode === "expanded");
  for (const cell of host.querySelectorAll<HTMLElement>(".gm-rail-preview-cell")) {
    const id = cell.dataset.railId as HomeRailMenuId | undefined;
    cell.classList.toggle("gm-rail-preview-cell--selected", Boolean(id && id === selectedId));
  }
}
